// Ortak onay diyaloğu (Modal üzerine) — GL12'den beri `@isgportal/shared`'da. Yıkıcı aksiyonlar
// (ör. belge kaldırma) için; `loading` sırasında butonlar kilitlenir, hata Alert ile i18n'e çözülür.
// Yerine geçtiği SP ve EP kopyaları birbirinden ayrışmıştı: EP'de `errorFallback` yoktu, `body`
// `<p>` içine sarılıyordu (blok içerik için geçersiz HTML) ve `admin.common.*` etiketleri kullanılıyordu.

import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '../i18n/apiError';
import { Alert } from './Alert';
import { Button } from './Button';
import { Modal } from './Modal';
import { Spinner } from './Spinner';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  /**
   * Vazgeçme butonunun etiketi. Varsayılan `common.cancel` ("Vazgeç") ve çoğu yıkıcı onay
   * için doğrusu odur. Kullanıcının KENDİ yarım kalmış işine geri döndüğü diyaloglarda
   * (ör. yükleme penceresini kapatma onayı) "Vazgeç" rolleri ters çevirir — orada bu prop
   * ile "Geri dön" verilir.
   */
  cancelLabel?: string;
  /** Onay butonu tonu (varsayılan danger — yıkıcı işlem). */
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  loading?: boolean;
  error?: unknown;
  /** Açan denetimin ref'i — kapanışta odak buraya döner (menü öğesi DOM'dan kalkar). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /**
   * `resolveApiErrorMessage`'ın bilinmeyen/eşlenmemiş bir `errorCode` için döneceği yedek —
   * varsayılan `error.generic`. Aksiyona özgü, daha nazik bir yedek metin geçmek içindir
   * (S24 — `analysis.discard.failed`); bilinen kodlar (ör. `DOCUMENT_ANALYSIS_NOT_TERMINAL`)
   * yine KENDİ yerel yedeğini kullanır, bu yalnız GERÇEKTEN eşlenemeyen kod/ağ hatasını kapsar.
   */
  errorFallback?: string;
  /** Verilirse `error` yerine bu hazır metin gösterilir (koda göre çözümün ezilmesi gereken yüzeyler için). */
  errorMessage?: string;
  /** Onay butonunu kilitler (ör. hedef artık yok: ikinci tıklama aynı hatayı tekrarlar). Vazgeç açık kalır. */
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  body,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'danger',
  onConfirm,
  loading,
  error,
  restoreFocusRef,
  errorFallback,
  errorMessage,
  confirmDisabled,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      restoreFocusRef={restoreFocusRef}
      title={title}
      description={description}
      size="sm"
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {loading ? <Spinner label={confirmLabel} /> : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? (
          <Alert tone="danger">
            {errorMessage ?? resolveApiErrorMessage(error, t, errorFallback)}
          </Alert>
        ) : null}
        {body ? <div className="text-sm text-fg">{body}</div> : null}
      </div>
    </Modal>
  );
}
